import React, { useEffect } from 'react';
import { X, CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  baseCheckoutUrl: string;
}
export function UpsellModal({
  isOpen,
  onClose,
  baseCheckoutUrl
}: UpsellModalProps) {
  // Pack completo (cenas + colágeno)
  const packCheckoutUrl = 'https://nutrinatural.online/cart/54561567277385:1';
  // Solo cenas antiinflamatorias
  const cenasOnlyUrl = 'https://nutrinatural.online/cart/54561516847433:1';
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-dark/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="upsell-modal-title" data-id="element-509">
      <div className="relative w-full max-w-md md:max-w-lg max-h-[95vh] overflow-y-auto bg-cream rounded-2xl shadow-2xl border-2 border-gold/40 animate-[scaleIn_0.25s_ease-out]" onClick={e => e.stopPropagation()} data-id="element-510">
        {/* Close button */}
        <button onClick={onClose} aria-label="Cerrar" className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-dark/60 hover:text-dark flex items-center justify-center shadow-sm transition-colors" data-id="element-511">
          <X size={18} data-id="element-512" />
        </button>

        {/* Urgency banner */}
        <div className="bg-urgency text-white py-2 px-4 text-center font-bold text-[11px] sm:text-xs uppercase tracking-wider rounded-t-2xl flex items-center justify-center gap-1.5" data-id="element-513">
          <Sparkles size={14} className="animate-pulse" data-id="element-514" />
          Oferta exclusiva solo en este popup
        </div>

        <div className="p-4 sm:p-6" data-id="element-515">
          {/* Header */}
          <div className="text-center mb-3 sm:mb-4" data-id="element-516">
            <h2 id="upsell-modal-title" className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-forest leading-tight mb-1.5" data-id="element-517">
              ¡Espera! Tu pedido está casi listo
            </h2>
            <p className="text-xs sm:text-sm text-dark/70 leading-snug" data-id="element-518">
              Suma a tu pedido ahora con{' '}
              <strong className="text-urgency" data-id="element-519">descuento especial</strong> solo
              en este popup:
            </p>
          </div>

          {/* Product */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gold/30 shadow-sm mb-4" data-id="element-520">
            <div className="flex gap-3 sm:gap-4 items-start" data-id="element-521">
              <img src="https://cdn.magicpatterns.com/uploads/qAsm1fNAFRKqZeCuaUBV7k/colageno.png" alt="Alimentación Antienvejecimiento: Colágeno Natural" className="w-20 sm:w-24 md:w-28 aspect-square object-cover rounded-lg shrink-0 border border-gold/20" data-id="element-522" />
              <div className="flex-1 min-w-0" data-id="element-523">
                <h3 className="font-serif text-sm sm:text-base md:text-lg font-bold text-forest leading-tight mb-1" data-id="element-524">
                  Alimentación Antienvejecimiento: El Colágeno Natural
                </h3>
                <p className="text-[11px] sm:text-xs text-dark/70 leading-snug" data-id="element-525">
                  Para ti que ya intentaste todo y sigues viendo cómo tu piel
                  cede silenciosamente cada año. No es otra dieta. Es la
                  bioquímica que nadie te explicó.
                </p>
              </div>
            </div>

            {/* Price */}
            <div className="mt-3 pt-3 border-t border-forest/10 flex items-center justify-between gap-3" data-id="element-526">
              <div className="flex items-center gap-2" data-id="element-527">
                <span className="bg-urgency text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-528">
                  50% OFF
                </span>
                <span className="text-xs sm:text-sm text-dark/40 line-through font-medium" data-id="element-529">
                  13,80 €
                </span>
              </div>
              <div className="text-right" data-id="element-530">
                <p className="text-[10px] sm:text-xs text-dark/60 leading-none mb-0.5" data-id="element-531">
                  Hoy solo
                </p>
                <p className="text-xl sm:text-2xl font-serif font-bold text-success leading-none" data-id="element-532">
                  6,90 €
                </p>
              </div>
            </div>
          </div>

          {/* Primary CTA — green */}
          <a href={packCheckoutUrl} target="_blank" rel="noopener noreferrer" className="group relative w-full flex flex-col items-center justify-center min-h-[54px] sm:min-h-[60px] px-4 py-3 rounded-xl font-bold text-sm sm:text-base bg-success text-white shadow-lg hover:bg-success/90 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden text-center leading-tight" data-id="element-533">
            <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" data-id="element-534"></div>
            <span className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2" data-id="element-535">
              <span className="flex items-center gap-1.5" data-id="element-536">
                <CheckCircle2 size={18} className="shrink-0" data-id="element-537" />
                QUIERO EL PACK COMPLETO
              </span>
              <span className="text-sm sm:text-base font-bold" data-id="element-538">23,90 €</span>
            </span>
          </a>

          {/* Trust line */}
          <div className="flex items-center justify-center gap-1.5 mt-2 text-[10px] sm:text-xs text-dark/60 font-medium" data-id="element-539">
            <ShieldCheck size={12} className="text-forest" data-id="element-540" />
            <span data-id="element-541">Garantía 30 días • Acceso inmediato</span>
          </div>

          {/* Secondary CTA — gray */}
          <a href={cenasOnlyUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center mt-3 sm:mt-4 py-2.5 px-4 rounded-lg text-xs sm:text-sm font-medium text-dark/60 hover:text-dark/80 bg-dark/5 hover:bg-dark/10 transition-colors underline-offset-2 hover:underline" data-id="element-542">
            Continuar solo con Cenas Antiinflamatorias
          </a>
        </div>
      </div>
    </div>;
}